import * as d3Path from 'd3-path'

const constant = function (x) {
  return function constant () {
    return x
  }
}

// Default accessors
function arcInnerRadius (d) {
  return d.innerRadius
}

function arcOuterRadius (d) {
  return d.outerRadius
}

function arcStartAngle (d) {
  return d.startAngle
}

function arcEndAngle (d) {
  return d.endAngle
}

function arrowIndent (d) {
  return d.indent
}

const headPoint = (a0, a1, r0, r1, _indent) => {
  let outer = {
      x: Math.cos(a1) * r1,
      y: Math.sin(a1) * r1
    }
    , inner = {
      x: Math.cos(a1) * r0,
      y: Math.sin(a1) * r0
    }
    , midpoint = {
      x: (inner.x + outer.x) / 2,
      y: (inner.y + outer.y) / 2
    }
    , m0 = (inner.y - outer.y) / (inner.x - outer.x)

  m0 = 1 / m0

  let k0 = _indent / Math.sqrt( 1 + ( m0 * m0 ))
    , point = {}

  if (Math.abs(m0) > 1000 * Math.PI) {
    point.x = midpoint.x
    if (inner.x < 0) {
      point.y = midpoint.y - _indent
    } else {
      point.y = midpoint.y + _indent
    }
  } else {
    let angle = a1

    while (angle < 0) {
      angle += Math.PI * 2
    }

    if (angle % (Math.PI * 2) < Math.PI) {
      point.x = midpoint.x - k0
      point.y = midpoint.y + (m0 * k0)
    } else {
      point.x = midpoint.x + k0
      point.y = midpoint.y - (m0 * k0)
    }
  }

  return point
}

const tailOuter = (a0, r1) => {
  return {
    x: Math.cos(a0) * r1,
    y: Math.sin(a0) * r1
  }
}

const tailPoint = (a0, a1, r0, r1, _indent) => {
  let outer = tailOuter(a0, r1)
    , inner = {
      x: Math.cos(a0) * r0,
      y: Math.sin(a0) * r0,
    }
    , midpoint = {
      x: (inner.x + outer.x) / 2,
      y: (inner.y + outer.y) / 2
    }
    , m1 = (inner.y - outer.y) / (inner.x - outer.x)

  m1 = 1 / m1

  let k1 = _indent / Math.sqrt( 1 + ( m1 * m1 ))
    , point = {}

  if (Math.abs(m1) > 1000 * Math.PI) {
    point.x = midpoint.x
    if (inner.x < 0) {
      point.y = midpoint.y - _indent
    } else {
      point.y = midpoint.y + _indent
    }
  } else {
    if (a0 > 0 && a0 < Math.PI) {
      point.x = midpoint.x - k1
      point.y = midpoint.y + (k1 * m1)
    } else {
      point.x = midpoint.x + k1
      point.y = midpoint.y - (k1 * m1)
    }
  }

  return point
}

/*
 * Draws the arrow tip between two points, p0 and p1. The points will connect
 * at the control, but using a slight quadratic curve
 */
const arrow = (p0, p1, control, context) => {
  let radius = 2
    , length = Math.sqrt(Math.pow(p0.x - control.x, 2) + Math.pow(p0.y - control.y, 2))

  let v0 = {
      x: control.x + (control.x - p0.x) / length * -radius,
      y: control.y + (control.y - p0.y) / length * -radius
    }
    , v1 = {
      x: control.x + (control.x - p1.x) / length * -radius,
      y: control.y + (control.y - p1.y) / length * -radius
    }

  // Draw a line to the shorter vector heading towards the control point
  context.lineTo(v0.x, v0.y)

  // Smooth curve around the control
  context.quadraticCurveTo(control.x, control.y, v1.x, v1.y)

  // Line to the next point
  context.lineTo(p1.x, p1.y)
}

/*
 * This draws a ring of arrows, similar to a donut chart but where the tips
 * have arrows. We use this drawing on cycle charts. A lot of this math and
 * code is ripped from d3-shape arcs.
 */
export default function () {
  let innerRadius = arcInnerRadius
    , outerRadius = arcOuterRadius
    , startAngle = arcStartAngle
    , endAngle = arcEndAngle
    , indent = arrowIndent

  function arc () {
    let context = d3Path.path()
      , _indent = indent.apply(this, arguments)
      , r0 = +innerRadius.apply(this, arguments) // inner radius
      , r1 = +outerRadius.apply(this, arguments) // outer radius
      , a0 = startAngle.apply(this, arguments) - Math.PI / 2
      , a1 = endAngle.apply(this, arguments) - Math.PI / 2
      , head = headPoint(a0, a1, r0, r1, _indent)
      , tail = tailPoint(a0, a1, r0, r1, _indent)
      , tailArrow1 = tailOuter(a0, r1) // Final spot of the tail

    // start at outer curve tail
    context.moveTo(tailArrow1.x, tailArrow1.y)

    // Create an arc to the outer curve head
    context.arc(0, 0, r1, a0, a1, false)

    let headArrow0 = { // outer point of the head
        x: context._x1,
        y: context._y1
      }
      , headArrow1 = { // This is the inner point of the head
        x: r0 * Math.cos(a1),
        y: r0 * Math.sin(a1)
      }

    // Draw the arrow tip
    arrow(headArrow0, headArrow1, head, context)

    // line to inner curve head, then arc to the inner curve tail
    context.arc(0, 0, r0, a1, a0, true)

    let tailArrow0 = { // inner point of the tail
        x: context._x1,
        y: context._y1
      }

    // Draw the arrow on the tail
    arrow(tailArrow0, tailArrow1, tail, context)

    context.closePath()

    return context.toString()
  }

  arc.centroid = function () {
    let {head, tail} = arc.headTailPoints.apply(this, arguments)
      , c0 = Math.atan2(tail.y, tail.x)
      , c1 = Math.atan2(head.y, head.x)

    if (c0 < 0) {
      c0 += Math.PI * 2
    }

    if (c1 < 0) {
      c1 += Math.PI * 2
    }

    if (c1 < c0) {
      // Add another rotation to c1 so it ends up after the tail
      c1 += Math.PI * 2
    }

    let r = (+innerRadius.apply(this, arguments) + +outerRadius.apply(this, arguments)) / 2
      , a = (c0 + c1) / 2 - Math.PI / 2

    return [Math.cos(a) * r, Math.sin(a) * r]
  }

  /*
   * Returns an object containing the head and tail points
   */
  arc.headTailPoints = function () {
    let a0 = +startAngle.apply(this, arguments)
      , a1 = +endAngle.apply(this, arguments)
      , r0 = +innerRadius.apply(this, arguments)
      , r1 = +outerRadius.apply(this, arguments)
      , _indent = indent() * .5 // I honestly don't know why this is needed, but my math must be off. This makes it look more centered
      , head = headPoint(a0, a1, r0, r1, _indent)
      , tail = tailPoint(a0, a1, r0, r1, _indent)

    return {head, tail}
  }

  /*
   * Returns the distance between the head and tail points
   */
  arc.distance = function () {
    let {head, tail} = arc.headTailPoints.apply(this, arguments)
      , a = head.x - tail.x
      , b = head.y - tail.y

    return Math.sqrt(a * a + b * b)
  }

  arc.innerRadius = function(_) {
    return arguments.length ? (innerRadius = typeof _ === 'function' ? _ : constant(+_), arc) : innerRadius
  }

  arc.outerRadius = function(_) {
    return arguments.length ? (outerRadius = typeof _ === 'function' ? _ : constant(+_), arc) : outerRadius
  }

  arc.startAngle = function(_) {
    return arguments.length ? (startAngle = typeof _ === 'function' ? _ : constant(+_), arc) : startAngle
  }

  arc.endAngle = function(_) {
    return arguments.length ? (endAngle = typeof _ === 'function' ? _ : constant(+_), arc) : endAngle
  }

  arc.indent = function (_) {
    return arguments.length ? (indent = typeof _ === 'function' ? _ : constant(+_), arc) : indent
  }

  return arc
}
